import classNames from 'classnames';
import React, { FC, HTMLAttributes, ReactNode } from 'react';

import { Motion } from '@sbu/components.motion';

import { ListItem } from 'shared/components/layout/List';
import { Slant } from 'shared/components/utils/Slant';

import css from './Row.module.scss';

interface RowProps
  extends Pick<
    HTMLAttributes<HTMLDivElement>,
    'className' | 'onMouseEnter' | 'onMouseLeave' | 'style'
  > {
  animated?: boolean;
  containerClassName?: string;
  dataSumoTest?: string;
  forceHover?: boolean;
  gridRowClassName?: string;
  hover?: boolean;
  outerClassName?: string;
  secondaryRow?: ReactNode;
  secondaryRowClassName?: string;
}

const RowInnerContents: FC<
  React.PropsWithChildren<
    Pick<
      RowProps,
      | 'className'
      | 'forceHover'
      | 'gridRowClassName'
      | 'hover'
      | 'onMouseEnter'
      | 'onMouseLeave'
      | 'secondaryRow'
      | 'secondaryRowClassName'
      | 'style'
    >
  >
> = ({
  children,
  className,
  gridRowClassName,
  hover = false,
  forceHover = false,
  secondaryRow,
  secondaryRowClassName,
  ...rest
}) => {
  const secondaryRowClasses = classNames(
    css.secondaryRow,
    secondaryRowClassName
  );

  return secondaryRow ? (
    <div
      className={classNames(
        css.row,
        { [css.hover]: hover, [css.forceHover]: forceHover },
        className
      )}
      {...rest}
    >
      <div className={classNames(css.gridRow, gridRowClassName)}>
        {children}
      </div>
      {!!secondaryRow && (
        <div className={secondaryRowClasses}>{secondaryRow}</div>
      )}
    </div>
  ) : (
    <div
      className={classNames(
        css.row,
        css.gridRow,
        { [css.hover]: hover, [css.forceHover]: forceHover },
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

const RowContents: FC<React.PropsWithChildren<RowProps>> = ({
  outerClassName,
  ...rest
}) => {
  return (
    <ListItem className={outerClassName}>
      <Slant>
        <RowInnerContents {...rest} />
      </Slant>
    </ListItem>
  );
};

export const SimpleRow: FC<React.PropsWithChildren<RowProps>> = (props) => (
  <Row {...props} animated={false} />
);

export const Row: FC<React.PropsWithChildren<RowProps>> = ({
  animated = true,
  containerClassName,
  dataSumoTest = 'row',
  ...rest
}) => {
  return animated ? (
    <Motion
      className={containerClassName}
      data-sumo-test={dataSumoTest}
      type='item'
    >
      <RowContents {...rest} />
    </Motion>
  ) : (
    <div className={containerClassName} data-sumo-test={dataSumoTest}>
      <RowContents {...rest} />
    </div>
  );
};
